import React, { useEffect, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  GridItem,
  Input,
  useColorModeValue,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useGooglePlacesAutocomplete } from 'hooks/useGooglePlacesAutocomplete';
import { parseGooglePlaceAddress } from 'utils/GooglePlaceAddress';
import MapView from 'components/detailMapData/MapView';

const GoogleMapAddress = ({
  coordinates,
  setCoordinates,
  initialAddress,
  onAddressChange,
  validationErrors,
  brandStars,
}) => {
  const textColor = useColorModeValue('navy.700', 'white');
  const [suggestions, setSuggestions] = useState([]);
  const [isMarkerLocked, setIsMarkerLocked] = useState(false);
  const [address, setAddress] = useState(initialAddress);

  const { getPlacePredictions, getPlaceDetails } =
    useGooglePlacesAutocomplete();

  const handleAddressChange = (event) => {
    const input = event.target.value;
    setAddress(input);
    onAddressChange(input);

    if (input) {
      getPlacePredictions({ input }, (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      });
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (placeId) => {
    getPlaceDetails(
      {
        placeId,
        fields: ['geometry', 'formatted_address', 'address_components'],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          setCoordinates({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
          setAddress(place.formatted_address);

          const addressComponents = parseGooglePlaceAddress(place);

          onAddressChange(place.formatted_address, addressComponents);
          setSuggestions([]);
          setIsMarkerLocked(false);
        }
      },
    );
  };

  useEffect(() => {
    setAddress(initialAddress);
  }, [initialAddress]);

  return (
    <>
      <Box position="relative">
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          fontWeight="500"
          color={textColor}
          mb="8px"
        >
          Address <Text color={brandStars}>*</Text>
        </FormLabel>
        <Input
          variant="auth"
          fontSize="sm"
          mb="2px"
          fontWeight="500"
          size="lg"
          value={address}
          onChange={handleAddressChange}
          placeholder="Enter an address"
        />
        {validationErrors?.address && (
          <p style={{ color: 'red', fontSize: '14px' }}>
            {validationErrors?.address?.message}
          </p>
        )}
        {suggestions.length > 0 && (
          <Box
            border="1px"
            position="absolute"
            zIndex="1"
            bg="white"
            borderColor="gray.200"
            borderRadius="md"
            maxH="200px"
            overflowY="auto"
            width="100%"
          >
            <List spacing={1}>
              {suggestions.map((suggestion) => (
                <ListItem
                  key={suggestion.place_id}
                  onClick={() => handleSuggestionClick(suggestion.place_id)}
                  cursor="pointer"
                  padding="8px"
                  _hover={{ backgroundColor: 'gray.100' }}
                >
                  {suggestion.description}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
      <GridItem colSpan={{ base: 1, lg: 3 }}>
        <MapView
          coordinates={[coordinates.lng, coordinates.lat]}
          isChange={true}
          setCoordinates={(event) => {
            if (isMarkerLocked) {
              setCoordinates(event);
              setAddress('');
              setSuggestions([]);
            }
          }}
          isReadOnly={!isMarkerLocked}
        />
      </GridItem>
    </>
  );
};

GoogleMapAddress.propTypes = {
  coordinates: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  setCoordinates: PropTypes.func.isRequired,
  initialAddress: PropTypes.string.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  editmerchantId: PropTypes.string,
  validationErrors: PropTypes.object,
  brandStars: PropTypes.string,
};

export default GoogleMapAddress;
